import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import ActionText from '../core/ActionText';
import Errored from '../core/Errored';
import Loading from '../core/Loading';
import { globalStyles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { makeCancelable, sortObjects } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import CommonIriDisplay from '../application-core/CommonIriDisplay';

const styles = () => {
    return Object.assign({
        treeNumber: {
            height: 30,
            backgroundColor: '#f5f5f5',
            maxWidth: 28,
            marginTop: -30,
            position: 'relative',
            textAlign: 'right'
        }
    }, globalStyles);
};

class InboundToResource extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isErrored: null,
            isLoading: true,
            results: []
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.getInbound();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iriList, options) {
        this.cancellable = makeCancelable(GS.loadData(iriList, options, this.props.graph, false));
        return this.cancellable;
    }

    doSearch(payload) {
        this.cancellable = makeCancelable(GS.search(payload));
        return this.cancellable;
    }

    getInbound = async () => {
        try {
            const data = await this.doSearch({
                search: [
                    {
                        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                        value: [
                            "http://www.w3.org/2002/07/owl#ObjectProperty",
                            "http://www.w3.org/2002/07/owl#DatatypeProperty",
                            "http://www.w3.org/2002/07/owl#AnnotationProperty"
                        ]
                    },
                    {
                        predicate: [
                            "http://www.w3.org/2000/01/rdf-schema#domain",
                            "http://www.w3.org/2000/01/rdf-schema#range"
                        ],
                        value: this.props.id
                    }
                ],
                graph: [this.props.graph],
                page: 1,
                pageSize: 200,
                basicSort: true,
                sort: "http://www.w3.org/2000/01/rdf-schema#label"
            }).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const results = data.results;
            const resultIRIs = results.map(res => res.id);
            // Load in one go
            await this.loadData(resultIRIs, {}).promise;
            this.setState({ results, isLoading: false });
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isErrored: e });
        }
    };

    render() {

        const { classes, data, projectIRI, graph, defaultLang, componentIRI, id, viewLang, components } = this.props;
        const { results, isLoading, isErrored } = this.state;

        if (isErrored) return <Errored dataTest="inbound-error" data={isErrored} />;
        if (isLoading) return <div style={{ height: 280 }}><Loading offset="130px" /></div>;
        if (data._notAvailable) return <ActionText offset="30px">Inbound information is not available.</ActionText>;

        const sortedResults = sortObjects(results, "label", viewLang, defaultLang, false);
        const objectPropsDomain = sortedResults.filter(res => res.domain && res.domain.includes(id) && res.type.includes("ObjectProperty"));
        const dataPropsDomain = sortedResults.filter(res => res.domain && res.domain.includes(id) && res.type.includes("DatatypeProperty"));
        const annotationPropsDomain = sortedResults.filter(res => res.domain && res.domain.includes(id) && res.type.includes("AnnotationProperty"));
        const objectPropsRange = sortedResults.filter(res => res.range && res.range.includes(id) && res.type.includes("ObjectProperty"));

        return (
            <div style={{ marginTop: 10 }}>
                <Typography paragraph className={classes.mainHeading} style={{ marginTop: 10 }}>Inbound Relationships</Typography>
                {results.length >= 200 &&
                    <Typography variant="body2">Showing the first 200 inbound relationships (by label order).</Typography>
                }
                <Typography paragraph className={classes.subMainHeading} style={{ marginTop: 10 }}>In Domain Of</Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                        <TitledPanel dataTest="obj-prop-domain" height="full" title="Object Properties">
                            <div style={{ width: '100%' }}>
                                {objectPropsDomain.map(resource =>
                                    <CommonIriDisplay
                                        id={resource.id}
                                        key={resource.id}
                                        components={components}
                                        projectIRI={projectIRI}
                                        context={componentIRI}
                                        contextProperty="isDefinedBy"
                                        handleNotAvailable={true}
                                        isReadOnly={true}
                                        compact={true}
                                        graph={graph}
                                        wrap={false}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang} />
                                )}
                            </div>
                        </TitledPanel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TitledPanel dataTest="data-prop-domain" height="full" title="Datatype Properties">
                            <div style={{ width: '100%' }}>
                                {dataPropsDomain.map(resource =>
                                    <CommonIriDisplay
                                        id={resource.id}
                                        key={resource.id}
                                        components={components}
                                        projectIRI={projectIRI}
                                        context={componentIRI}
                                        contextProperty="isDefinedBy"
                                        handleNotAvailable={true}
                                        isReadOnly={true}
                                        compact={true}
                                        graph={graph}
                                        wrap={false}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang} />
                                )}
                            </div>
                        </TitledPanel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TitledPanel dataTest="ann-prop-domain" height="full" title="Annotation Properties">
                            <div style={{ width: '100%' }}>
                                {annotationPropsDomain.map(resource =>
                                    <CommonIriDisplay
                                        id={resource.id}
                                        key={resource.id}
                                        components={components}
                                        projectIRI={projectIRI}
                                        context={componentIRI}
                                        contextProperty="isDefinedBy"
                                        handleNotAvailable={true}
                                        isReadOnly={true}
                                        compact={true}
                                        graph={graph}
                                        wrap={false}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang} />
                                )}
                            </div>
                        </TitledPanel>
                    </Grid>
                </Grid>
                <Typography paragraph className={classes.subMainHeading} style={{ marginTop: 24 }}>In Range Of</Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                        <TitledPanel dataTest="obj-prop-range" height="full" title="Object Properties">
                            <div style={{ width: '100%' }}>
                                {objectPropsRange.map(resource =>
                                    <CommonIriDisplay
                                        id={resource.id}
                                        key={resource.id}
                                        components={components}
                                        projectIRI={projectIRI}
                                        context={componentIRI}
                                        contextProperty="isDefinedBy"
                                        handleNotAvailable={true}
                                        isReadOnly={true}
                                        compact={true}
                                        graph={graph}
                                        wrap={false}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang} />
                                )}
                            </div>
                        </TitledPanel>
                    </Grid>
                </Grid>
            </div>
        );
    }
}

InboundToResource.propTypes = {
    classes: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    viewLang: PropTypes.string,
    defaultLang: PropTypes.string,
    components: PropTypes.array,
    componentIRI: PropTypes.string,
    projectIRI: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    graph: PropTypes.string.isRequired
};

export default withStyles(styles)(withGS(InboundToResource, ["ALL"], null, {}, true));